import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { formatSavedAtLabel, formatSavedAtStamp } from 'app/shared/util/saved-timestamp.util';
import { getApiErrorMessage } from '../../../services/api-error.helper';
import { ProjectInstructions, ProjectInstructionsTab, ProjectInstructionsVersion } from '../../../models/project-instructions.model';
import { ProjectInstructionsService } from '../../../services/project-instructions.service';

const EMPTY_INSTRUCTIONS: ProjectInstructions = { projectId: '', text: '', savedAt: null, savedBy: null, version: 0, canEdit: false, characterLimit: 0, versions: [] };

@Component({
  selector: 'deo-project-instructions',
  standalone: false,
  templateUrl: './deo-project-instructions.component.html',
  styleUrls: ['./deo-project-instructions.component.scss']
})
export class DeoProjectInstructionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId = '';
  @Input() projectName = '';
  /** False renders the instructions read-only, whatever the backend says. Defaults true so the backend's `can_edit` decides. */
  @Input() canEdit = true;

  @Output() loadingChange$ = new EventEmitter<boolean>();
  @Output() instructionsSaved$ = new EventEmitter<void>();

  activeTab: ProjectInstructionsTab = 'instructions';
  text = '';
  isLoading = false;
  isSaving = false;
  loadError: string | null = null;

  private _saved: ProjectInstructions = EMPTY_INSTRUCTIONS;
  private readonly _destroy$ = new Subject<void>();
  private _loadSubscription?: Subscription;
  /** Moved on by every save and every project switch, so a save answering late cannot touch another project. */
  private _saveToken = 0;

  constructor(
    private readonly _instructionsService: ProjectInstructionsService,
    private readonly _toastService: GainsToastService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadInstructions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this._saveToken++;
      this.isSaving = false;
      this.loadInstructions();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Editing needs both the host's say-so and the backend's `can_edit`, which is the one that is enforced. */
  get isEditable(): boolean {
    return this.canEdit && this._saved.canEdit;
  }

  /** A save in flight puts the submitted text back in the box when it lands, so nothing may change it meanwhile. */
  get canChangeText(): boolean {
    return this.isEditable && !this.isSaving;
  }

  get characterLimit(): number {
    return this._saved.characterLimit;
  }

  get characterCount(): number {
    return this.text.length;
  }

  get isOverLimit(): boolean {
    return this.characterCount > this.characterLimit;
  }

  get charactersOverLimit(): number {
    return Math.max(0, this.characterCount - this.characterLimit);
  }

  /** How full the meter is; capped so a very long draft does not overflow its track. */
  get meterPercentage(): number {
    if (this.characterLimit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.characterCount / this.characterLimit) * 100));
  }

  get isDirty(): boolean {
    return this.text !== this._saved.text;
  }

  get isSaveBlocked(): boolean {
    return !this.isDirty || this.isOverLimit || this.isSaving;
  }

  get versions(): ProjectInstructionsVersion[] {
    return this._saved.versions;
  }

  get subtitle(): string {
    const name = this.projectName?.trim();
    return name
      ? `Provide DEO with relevant instructions and information for chats within ${name}.`
      : 'Provide DEO with relevant instructions and information for chats within this project.';
  }

  get lastSavedLabel(): string {
    if (!this._saved.savedAt) {
      return 'Never saved';
    }

    const when = formatSavedAtStamp(this._saved.savedAt);
    const who = this._saved.savedBy ? ` by ${this._saved.savedBy}` : '';
    return when ? `Last saved ${when}${who}` : `Last saved${who}`;
  }

  get showEditor(): boolean {
    return this.activeTab === 'instructions';
  }

  formatCount(value: number): string {
    return value.toLocaleString('en-US');
  }

  versionSavedLabel(version: ProjectInstructionsVersion): string {
    return formatSavedAtLabel(version.savedAt) || '--';
  }

  trackByVersionTag(index: number, version: ProjectInstructionsVersion): string {
    return version.tag;
  }

  changeActiveTab(tab: ProjectInstructionsTab): void {
    this.activeTab = tab;
  }

  onTabIndexChange(index: number): void {
    this.changeActiveTab(index === 1 ? 'history' : 'instructions');
  }

  get activeTabIndex(): number {
    return this.activeTab === 'history' ? 1 : 0;
  }

  onTextChange(value: string): void {
    if (!this.canChangeText) {
      return;
    }
    this.text = value;
    this.rememberDraft();
  }

  discard(): void {
    this.text = this._saved.text;
    this._instructionsService.clearDraft(this.projectId);
  }

  save(): void {
    if (!this.isEditable || this.isSaveBlocked) {
      return;
    }

    this.isSaving = true;
    const token = ++this._saveToken;
    this._instructionsService
      .saveInstructions(this.projectId, this.text)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: saved => {
          if (this.isCurrentSave(token)) {
            this.afterSave(saved);
          }
        },
        error: error => {
          if (this.isCurrentSave(token)) {
            this.afterSaveFailed(error);
          }
        }
      });
  }

  /**
   * Puts a version's text back in the box as an unsaved change rather than saving it.
   *
   * Restoring is a proposal, not a decision — the reviewer still reads it and presses Save.
   */
  restoreVersion(version: ProjectInstructionsVersion): void {
    if (!this.canChangeText) {
      return;
    }
    this.text = version.text;
    this.rememberDraft();
    this.changeActiveTab('instructions');
  }

  retryLoad(): void {
    this.loadInstructions();
  }

  private isCurrentSave(token: number): boolean {
    return token === this._saveToken;
  }

  private afterSave(saved: ProjectInstructions): void {
    this._saved = saved;
    this.text = saved.text;
    this.isSaving = false;
    this._toastService.success('Instructions Saved', 'DEO will use them from the next message.');
    this.instructionsSaved$.emit();
    this._cdr.markForCheck();
  }

  private afterSaveFailed(error: unknown): void {
    this.isSaving = false;
    const status = error instanceof HttpErrorResponse ? error.status : 0;

    if (status === 409) {
      this._toastService.error('Changed Elsewhere', 'These instructions were changed elsewhere. The latest version is now shown.');
      this.loadInstructions();
      return;
    }

    if (status === 403) {
      this._saved = { ...this._saved, canEdit: false };
    }
    this._toastService.error('Save Failed', getApiErrorMessage(error, 'Could not save the instructions. Please try again.'));
    this._cdr.markForCheck();
  }

  private loadInstructions(): void {
    this._loadSubscription?.unsubscribe();
    if (!this.projectId) {
      this.applyLoaded(EMPTY_INSTRUCTIONS);
      this.loadingChange$.emit(false);
      return;
    }

    this.isLoading = true;
    this.loadError = null;
    this.loadingChange$.emit(true);

    this._loadSubscription = this._instructionsService
      .loadInstructions(this.projectId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: instructions => {
          this.applyLoaded(instructions);
          this.finishLoading();
        },
        error: error => {
          this.loadError = getApiErrorMessage(error, 'Could not load the instructions for this project.');
          this.finishLoading();
        }
      });
  }

  private applyLoaded(instructions: ProjectInstructions): void {
    this._saved = instructions;

    const draft = this._instructionsService.draftFor(this.projectId);
    this.text = draft ?? instructions.text;
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.loadingChange$.emit(false);
    this._cdr.markForCheck();
  }

  /** Keeps unsaved text on the service, so switching to Chats and back does not lose it. */
  private rememberDraft(): void {
    this._instructionsService.setDraft(this.projectId, this.text);
  }
}
